/**
 * KS2-I3 — Artifact Detail Page
 * Route: /app/artifact/:id
 *
 * Displays full artifact detail from actual backend data only.
 * Nothing is fabricated. Missing fields render as "Not available".
 * Review actions: ACCEPT_FOR_EVIDENCE_REVIEW | REJECT | HOLD | ESCALATE
 * READY_FOR_HUMAN_REVIEW eligibility check control included.
 *
 * Identity Disclosure: Reviewer identity is preview-supplied.
 * KS-GATE-AUTH remains OPEN — not production-authenticated.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, CheckSquare, XSquare, PauseCircle, AlertTriangle,
  ChevronDown, ChevronUp, FileText, Shield, Clock, BookOpen, Lock,
} from 'lucide-react';
import api from '@/lib/api';
import { AdmissionBadge } from '@/components/AddSourceModal';

const fmt = (d) => {
  if (!d) return 'Not available';
  try { return new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return d; }
};

const NA = () => (
  <span className="font-body text-gray-400 italic" style={{ fontSize: '12px' }}>Not available</span>
);

// ─── Lifecycle Evaluation Badge ───────────────────────────────────────────────

const LIFECYCLE_DECISION_CONFIG = {
  BLOCKED_BY_HOLD:        { label: 'ACTIVE HOLD — DELETION BLOCKED', color: '#dc2626', bg: '#fee2e2', icon: Lock },
  ELIGIBLE_FOR_DELETION:  { label: 'ELIGIBLE FOR DELETION',          color: '#92400e', bg: '#fef3c7', icon: AlertTriangle },
  NOT_YET_ELIGIBLE:       { label: 'RETENTION — NOT YET ELIGIBLE',   color: '#1e40af', bg: '#dbeafe', icon: Shield },
  INDEFINITE:             { label: 'RETAINED INDEFINITELY',          color: '#374151', bg: '#f3f4f6', icon: Shield },
  NO_POLICY:              { label: 'NO RETENTION POLICY',            color: '#6b7280', bg: '#f9fafb', icon: Shield },
  UNRESOLVED:             { label: 'LIFECYCLE UNRESOLVED',           color: '#b45309', bg: '#fffbeb', icon: AlertTriangle },
};

function LifecycleBadge({ artifactId }) {
  const [evaluation, setEvaluation] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [loadState, setLoadState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    api.get(`/lifecycle/evaluate/artifact/${artifactId}`)
      .then(r => { if (!cancelled) { setEvaluation(r.data); setLoadState('loaded'); } })
      .catch(() => { if (!cancelled) setLoadState('error'); });

    api.get('/lifecycle/deletion-requests')
      .then(r => {
        if (cancelled) return;
        const active = (r.data.requests || []).find(
          req => req.target_resource_id === artifactId &&
                 !['COMPLETED', 'CANCELLED', 'FAILED'].includes(req.status)
        );
        setPendingRequest(active || null);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [artifactId]);

  if (loadState === 'loading') {
    return (
      <span className="font-body text-gray-400 italic" style={{ fontSize: '11px' }} data-testid="lifecycle-badge-loading">
        Loading lifecycle status…
      </span>
    );
  }
  if (loadState === 'error') return null;

  const decision = evaluation?.decision;
  const conf = LIFECYCLE_DECISION_CONFIG[decision] || LIFECYCLE_DECISION_CONFIG.NO_POLICY;
  const Icon = conf.icon;

  return (
    <div className="space-y-2" data-testid="lifecycle-badge-container">
      {/* Primary retention decision */}
      <div
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded"
        style={{ backgroundColor: conf.bg, border: `1px solid ${conf.color}22` }}
        data-testid="lifecycle-decision-badge"
      >
        <Icon size={12} style={{ color: conf.color, flexShrink: 0 }} />
        <span className="font-heading font-semibold" style={{ fontSize: '10px', letterSpacing: '0.06em', color: conf.color }}>
          {conf.label}
        </span>
        {decision === 'BLOCKED_BY_HOLD' && evaluation?.active_holds?.length > 0 && (
          <span className="font-mono" style={{ fontSize: '10px', color: conf.color }}>
            ({evaluation.active_holds.length} hold{evaluation.active_holds.length > 1 ? 's' : ''})
          </span>
        )}
        {decision === 'NOT_YET_ELIGIBLE' && evaluation?.eligible_at && (
          <span className="font-body" style={{ fontSize: '10px', color: conf.color }}>
            eligible {new Date(evaluation.eligible_at).toLocaleDateString('en-US', { dateStyle: 'medium' })}
          </span>
        )}
      </div>

      {/* Pending deletion request warning */}
      {pendingRequest && (
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded ml-2"
          style={{ backgroundColor: '#fff5f5', border: '1px solid #fca5a5' }}
          data-testid="pending-deletion-badge"
        >
          <AlertTriangle size={12} style={{ color: '#dc2626', flexShrink: 0 }} />
          <span className="font-heading font-semibold" style={{ fontSize: '10px', letterSpacing: '0.06em', color: '#dc2626' }}>
            DELETION {pendingRequest.status.replace(/_/g, ' ')}
          </span>
          <Link
            to="/app/lifecycle"
            className="font-heading font-medium underline"
            style={{ fontSize: '10px', color: '#dc2626' }}
            data-testid="lifecycle-link-from-badge"
          >
            View →
          </Link>
        </div>
      )}

      {/* Policy info line */}
      {evaluation?.policy_name && (
        <p className="font-body text-gray-400" style={{ fontSize: '11px' }} data-testid="lifecycle-policy-line">
          Policy: {evaluation.policy_name}
          {evaluation.retention_days ? ` · ${evaluation.retention_days}d` : ' · indefinite'}
        </p>
      )}
    </div>
  );
}


// ─── Review action form ───────────────────────────────────────────────────────

const NOTE_REQUIRED_ACTIONS = ['reject', 'hold', 'escalate'];

function ReviewActionForm({ artifactId, onSuccess }) {
  const [action, setAction]       = useState('');
  const [note, setNote]           = useState('');
  const [reviewer, setReviewer]   = useState('');
  const [status, setStatus]       = useState('idle');
  const [errMsg, setErrMsg]       = useState('');
  const [result, setResult]       = useState(null);

  const noteRequired = NOTE_REQUIRED_ACTIONS.includes(action);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!action) return;
    if (noteRequired && !note.trim()) {
      setErrMsg('A note is required for this action.');
      return;
    }
    if (!reviewer.trim()) {
      setErrMsg('Reviewer identity is required.');
      return;
    }
    setStatus('loading'); setErrMsg('');
    try {
      const r = await api.post(`/sources/artifacts/${artifactId}/review`, {
        action, note, reviewer, reviewer_role: 'Knowledge Steward',
      });
      setResult(r.data);
      setStatus('success');
      if (onSuccess) onSuccess();
    } catch (err) {
      setErrMsg(err.response?.data?.detail || 'Review action failed.');
      setStatus('idle');
    }
  }

  if (status === 'success') {
    return (
      <div
        className="rounded p-3 border"
        style={{ backgroundColor: '#f0fdf6', borderColor: '#bbf7d0' }}
        data-testid="review-success"
      >
        <p className="font-heading font-medium text-black" style={{ fontSize: '13px' }}>
          Review submitted — {result?.resulting_state}
        </p>
        <p className="font-body text-gray-500 mt-1" style={{ fontSize: '12px' }}>
          ACCEPT_FOR_EVIDENCE_REVIEW does not mean the source is accepted institutional evidence,
          canon-locked, or scientifically validated.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="review-action-form" className="space-y-3">
      <div>
        <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '11px', letterSpacing: '0.05em' }}>
          ACTION
        </label>
        <select
          value={action}
          onChange={e => { setAction(e.target.value); setErrMsg(''); }}
          className="w-full rounded border px-3 py-2 font-body bg-white"
          style={{ fontSize: '13px', borderColor: '#d9dee6' }}
          data-testid="review-action-select"
        >
          <option value="">Select action…</option>
          <option value="approve">ACCEPT_FOR_EVIDENCE_REVIEW</option>
          <option value="reject">REJECT</option>
          <option value="hold">HOLD</option>
          <option value="escalate">ESCALATE</option>
          <option value="safe_hold">SAFE_HOLD</option>
        </select>
      </div>

      <div>
        <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '11px', letterSpacing: '0.05em' }}>
          REVIEWER IDENTITY{' '}
          <span className="font-normal text-gray-400 normal-case" style={{ fontSize: '10px' }}>
            (preview identity — KS-GATE-AUTH open)
          </span>
        </label>
        <input
          type="text"
          value={reviewer}
          onChange={e => setReviewer(e.target.value)}
          placeholder="e.g. Jordan Mercer"
          className="w-full rounded border px-3 py-2 font-body"
          style={{ fontSize: '13px', borderColor: '#d9dee6' }}
          data-testid="review-reviewer-input"
        />
      </div>

      <div>
        <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '11px', letterSpacing: '0.05em' }}>
          NOTE{noteRequired ? ' (required)' : ' (optional)'}
        </label>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder={noteRequired ? 'A meaningful note is required for this action.' : 'Optional note…'}
          rows={3}
          className="w-full rounded border px-3 py-2 font-body"
          style={{ fontSize: '13px', borderColor: '#d9dee6', resize: 'vertical' }}
          data-testid="review-note-textarea"
        />
      </div>

      {errMsg && (
        <p className="font-body text-red-600 text-xs" data-testid="review-error">{errMsg}</p>
      )}

      <button
        type="submit"
        disabled={!action || status === 'loading'}
        className="font-heading font-semibold px-4 py-2 rounded transition-colors"
        style={{
          backgroundColor: action && status !== 'loading' ? '#000' : '#e5e5e5',
          color: action && status !== 'loading' ? '#fff' : '#a3a3a3',
          fontSize: '12px',
        }}
        data-testid="review-submit-btn"
      >
        {status === 'loading' ? 'Submitting…' : 'Submit Review'}
      </button>

      <p className="font-body text-gray-400" style={{ fontSize: '11px' }}>
        ACCEPT_FOR_EVIDENCE_REVIEW does not mean accepted institutional evidence,
        CANON_LOCKED, or scientifically validated.
      </p>
    </form>
  );
}

// ─── READY_FOR_HUMAN_REVIEW check control ────────────────────────────────────

function ReadyCheckControl({ artifactId, onTransitioned }) {
  const [status, setStatus]     = useState('idle');
  const [result, setResult]     = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  async function runCheck() {
    setStatus('loading');
    try {
      const r = await api.post(`/sources/artifacts/${artifactId}/ready-check`);
      setResult(r.data);
      setStatus('done');
      if (r.data.transitioned && onTransitioned) onTransitioned();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Check failed.' });
      setStatus('error');
    }
  }

  if (status === 'idle') {
    return (
      <button
        onClick={runCheck}
        className="inline-flex items-center gap-1.5 font-heading font-medium px-3 py-1.5 rounded border transition-colors"
        style={{ fontSize: '12px', borderColor: '#d9dee6' }}
        data-testid="ready-check-btn"
      >
        <Shield size={12} />
        Run Eligibility Check
      </button>
    );
  }

  if (status === 'loading') {
    return (
      <span className="font-mono text-gray-400" style={{ fontSize: '12px' }} data-testid="ready-check-loading">
        Evaluating…
      </span>
    );
  }

  if (status === 'error') {
    return (
      <p className="font-body text-red-600 text-xs" data-testid="ready-check-error">
        {result?.error}
      </p>
    );
  }

  return (
    <div className="rounded p-3 border" style={{ borderColor: result?.eligible ? '#bbf7d0' : '#fde8e8', backgroundColor: result?.eligible ? '#f0fdf6' : '#fef2f2' }} data-testid="ready-check-result">
      <div className="flex items-center justify-between gap-2">
        <p className="font-heading font-medium text-black" style={{ fontSize: '12px' }}>
          {result?.already_ready
            ? 'Already in READY_FOR_HUMAN_REVIEW (idempotent).'
            : result?.transitioned
              ? 'Transitioned → READY_FOR_HUMAN_REVIEW'
              : result?.eligible
                ? 'Eligible — no transition needed.'
                : `Not eligible. ${result?.failed_criteria?.length} criteria failed.`}
        </p>
        <button
          onClick={() => setShowDetail(v => !v)}
          className="font-body text-gray-500 hover:text-black text-xs"
          data-testid="ready-check-toggle-criteria"
        >
          {showDetail ? 'Hide' : 'Show'} criteria
        </button>
      </div>
      {showDetail && (
        <div className="mt-2 space-y-1" data-testid="ready-check-criteria">
          {(result?.criteria || []).map((c, i) => (
            <div key={i} className="flex items-start gap-2">
              <span style={{ color: c.passed ? '#03f37c' : '#dc2626', fontSize: '11px', flexShrink: 0, marginTop: '1px' }}>
                {c.passed ? '✓' : '✗'}
              </span>
              <div>
                <span className="font-heading font-medium text-black" style={{ fontSize: '11px' }}>{c.criterion}</span>
                <span className="font-body text-gray-500 ml-1" style={{ fontSize: '11px' }}>{c.note}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="font-body text-gray-500 mt-2" style={{ fontSize: '11px', fontStyle: 'italic' }}>
        {result?.disclaimer}
      </p>
    </div>
  );
}

// ─── Passage list ─────────────────────────────────────────────────────────────

function PassageList({ passages }) {
  const [expanded, setExpanded] = useState(false);
  if (!passages || passages.length === 0) {
    return <NA />;
  }
  const visible = expanded ? passages : passages.slice(0, 3);
  return (
    <div data-testid="passage-list">
      <div className="space-y-2">
        {visible.map((p, i) => (
          <div key={p.id || p.passage_id || i} className="rounded border p-3" style={{ borderColor: '#e5e5e5' }} data-testid={`passage-item-${i}`}>
            <div className="flex items-center gap-2 mb-1">
              {p.anchor && (
                <span className="font-heading text-gray-500" style={{ fontSize: '10px', letterSpacing: '0.05em' }}>
                  {p.anchor}
                </span>
              )}
              {p.hash && (
                <span className="font-mono text-gray-300" style={{ fontSize: '10px' }} title={p.hash}>
                  {p.hash.slice(0, 8)}…
                </span>
              )}
            </div>
            <p className="font-body text-gray-700" style={{ fontSize: '12px', lineHeight: 1.5 }}>
              {p.text || <NA />}
            </p>
          </div>
        ))}
      </div>
      {passages.length > 3 && (
        <button
          onClick={() => setExpanded(v => !v)}
          className="mt-2 font-body text-gray-500 hover:text-black text-xs flex items-center gap-1"
          data-testid="passage-expand-btn"
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? 'Show fewer' : `Show all ${passages.length} passages`}
        </button>
      )}
    </div>
  );
}

// ─── Review history timeline ──────────────────────────────────────────────────

function ReviewHistory({ history }) {
  if (!history || history.length === 0) {
    return <p className="font-body text-gray-400 text-sm">No review history recorded.</p>;
  }
  return (
    <div className="space-y-3" data-testid="review-history-list">
      {[...history].reverse().map((ev, i) => (
        <div key={ev.id || i} className="flex gap-3" data-testid={`review-event-${i}`}>
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full mt-1 shrink-0" style={{ backgroundColor: '#03f37c' }} />
            {i < history.length - 1 && <div className="w-0.5 flex-1 mt-1" style={{ backgroundColor: '#e5e5e5' }} />}
          </div>
          <div className="pb-3 flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-heading font-semibold text-black" style={{ fontSize: '12px' }}>
                {ev.action?.toUpperCase() || 'UNKNOWN'}
              </span>
              <span className="font-body text-gray-500" style={{ fontSize: '11px' }}>
                by {ev.reviewer || 'Unknown'}
              </span>
              <span className="font-body text-gray-400" style={{ fontSize: '11px' }}>
                {fmt(ev.timestamp)}
              </span>
            </div>
            <p className="font-body text-gray-600 mt-0.5" style={{ fontSize: '12px' }}>
              {ev.previous_state} → {ev.resulting_state}
            </p>
            {ev.note && (
              <p className="font-body text-gray-500 mt-0.5 italic" style={{ fontSize: '12px' }}>
                "{ev.note}"
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Audit event list ─────────────────────────────────────────────────────────

function AuditEventList({ events }) {
  if (!events || events.length === 0) {
    return <p className="font-body text-gray-400 text-sm">No audit events found for this artifact.</p>;
  }
  return (
    <div className="space-y-2" data-testid="audit-event-list">
      {events.map((ev, i) => (
        <div key={ev.id || i} className="rounded border p-3" style={{ borderColor: '#e5e5e5' }} data-testid={`audit-event-${i}`}>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-heading font-medium text-black" style={{ fontSize: '11px' }}>{ev.action}</span>
            <span className="font-body text-gray-400" style={{ fontSize: '11px' }}>{fmt(ev.timestamp)}</span>
          </div>
          <p className="font-body text-gray-600" style={{ fontSize: '12px' }}>
            Actor: {ev.actor} — Role: {ev.role || 'N/A'}
          </p>
          {ev.reason && (
            <p className="font-body text-gray-500 mt-0.5 italic" style={{ fontSize: '12px' }}>
              {ev.reason}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b" style={{ borderColor: '#e5e5e5' }}>
        {Icon && <Icon size={14} style={{ color: '#a3a3a3' }} />}
        <h2 className="font-heading font-semibold text-black" style={{ fontSize: '13px', letterSpacing: '0.04em' }}>
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, mono = false, testId }) {
  return (
    <div className="mb-2">
      <span className="font-heading font-medium text-gray-500 block" style={{ fontSize: '10px', letterSpacing: '0.06em', marginBottom: '2px' }}>
        {label}
      </span>
      {value !== undefined && value !== null && value !== '' ? (
        <span
          className={mono ? 'font-mono text-black' : 'font-body text-black'}
          style={{ fontSize: '13px' }}
          data-testid={testId}
        >
          {value}
        </span>
      ) : (
        <NA />
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function IngestionArtifact() {
  const { id } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [showReview, setShowReview] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/sources/artifacts/${id}/detail`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="py-8" data-testid="artifact-loading">
      <p className="font-body text-gray-400">Loading artifact…</p>
    </div>
  );

  if (error) return (
    <div className="py-8" data-testid="artifact-error">
      <p className="font-body text-gray-700">Error: {error}</p>
    </div>
  );

  const { artifact, canon_source, canon_versions, claims, constraints, review_history, audit_events, lineage_disclosure } = data || {};

  if (!artifact) return (
    <div className="py-8" data-testid="artifact-not-found">
      <p className="font-body text-gray-700">Artifact not found.</p>
    </div>
  );

  const passages = artifact.extracted_passages || [];

  return (
    <div data-testid="artifact-detail-page">
      {/* Back navigation */}
      <Link
        to="/app/canon"
        className="inline-flex items-center gap-1.5 font-body text-sm text-gray-400 hover:text-black mb-6 transition-colors"
        data-testid="back-to-canon"
      >
        <ArrowLeft size={14} />
        Canon / Ingestion queue
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-start gap-3 mb-2">
          <h1 className="page-heading" data-testid="artifact-title">{artifact.title || 'Untitled'}</h1>
          {artifact.admission_state && <AdmissionBadge state={artifact.admission_state} />}
        </div>
        <p className="font-body text-gray-500" style={{ fontSize: '14px' }}>
          {artifact.description || <NA />}
        </p>
        {/* Lifecycle evaluation badge — KS-PF2 */}
        <div className="mt-3" data-testid="lifecycle-status-section">
          <LifecycleBadge artifactId={id} />
        </div>
      </div>

      {/* READY_FOR_HUMAN_REVIEW control */}
      {!['CANON_LOCKED', 'QUARANTINED', 'SAFE_HOLD'].includes(artifact.admission_state) && (
        <div className="mb-6 p-4 rounded border" style={{ borderColor: '#e5e5e5', backgroundColor: '#fafafa' }}>
          <h3 className="font-heading font-semibold text-black mb-2" style={{ fontSize: '12px', letterSpacing: '0.04em' }}>
            MACHINE ELIGIBILITY CHECK
          </h3>
          <ReadyCheckControl artifactId={id} onTransitioned={load} />
        </div>
      )}

      {/* Identity */}
      <Section title="ARTIFACT IDENTITY" icon={FileText} testId="section-identity">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3">
          <Field label="ARTIFACT ID" value={artifact.id} mono testId="artifact-id" />
          <Field label="TENANT" value={artifact.tenant_id} testId="artifact-tenant" />
          <Field label="SOURCE TYPE" value={artifact.source_type} testId="artifact-source-type" />
          <Field label="OWNER" value={artifact.owner} testId="artifact-owner" />
          <Field label="SUBMITTED BY" value={artifact.submitted_by} testId="artifact-submitted-by" />
          <Field label="SUBMITTED" value={fmt(artifact.created_at)} testId="artifact-created-at" />
        </div>
      </Section>

      {/* Governance state */}
      <Section title="GOVERNANCE STATE" icon={Shield} testId="section-governance">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3">
          <Field label="ADMISSION STATE" value={artifact.admission_state} testId="artifact-admission-state" />
          <Field label="STATE REASON" value={artifact.admission_state_reason} />
          <Field label="SAFE HOLD RECOMMENDED" value={artifact.safe_hold_recommended !== undefined ? String(artifact.safe_hold_recommended) : undefined} />
        </div>
      </Section>

      {/* Integrity */}
      <Section title="INTEGRITY AND PROVENANCE" testId="section-integrity">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          <Field label="SHA-256" value={artifact.sha256} mono testId="artifact-sha256" />
          <Field label="MIME TYPE" value={artifact.mime_type} testId="artifact-mime-type" />
          <Field label="FILE SIZE" value={artifact.file_size_bytes !== undefined ? `${artifact.file_size_bytes.toLocaleString()} bytes` : undefined} />
          <Field label="ORIGINAL FILENAME" value={artifact.original_filename} />
        </div>
        {artifact.connector_provenance && (
          <div
            data-testid="connector-provenance-panel"
            style={{
              marginTop: '16px',
              padding: '12px 14px',
              background: '#0f1a2e',
              border: '1px solid #1e3a5f',
              borderRadius: '6px',
            }}
          >
            <div style={{ color: '#93c5fd', fontSize: '10px', letterSpacing: '0.06em', fontWeight: 700, marginBottom: '10px' }}>
              CONNECTOR PROVENANCE (KS-PF4)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              <Field label="CONNECTOR ID" value={artifact.connector_provenance.connector_id} mono />
              <Field label="SYNC RUN ID" value={artifact.connector_provenance.sync_run_id} mono />
              <Field label="EXTERNAL OBJECT ID" value={artifact.connector_provenance.external_object_id} mono />
            </div>
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#3b82f6' }}>
              This artifact was imported via a governed connector. It entered the normal admission
              state machine and has NOT been automatically promoted to canon or approved as evidence.
            </div>
          </div>
        )}
      </Section>

      {/* Source lineage */}
      <Section title="SOURCE / VERSION LINEAGE" icon={BookOpen} testId="section-lineage">
        {canon_source ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4">
              <div>
                <span className="font-heading font-medium text-gray-500 block" style={{ fontSize: '10px', letterSpacing: '0.06em', marginBottom: '2px' }}>
                  CANON SOURCE
                </span>
                <Link
                  to={`/app/canon/${canon_source.id}`}
                  className="font-heading font-medium text-black hover:underline"
                  style={{ fontSize: '13px' }}
                  data-testid="canon-source-link"
                >
                  {canon_source.title}
                </Link>
                <span className="ml-2 font-mono text-gray-400" style={{ fontSize: '10px' }}>
                  {canon_source.id?.slice(0, 8)}…
                </span>
              </div>
            </div>
            {(canon_versions || []).map((ver, i) => (
              <div key={ver.id || i} className="rounded border p-3" style={{ borderColor: '#e5e5e5' }} data-testid={`canon-version-${i}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-heading font-semibold text-black" style={{ fontSize: '12px' }}>
                    {ver.version_number || ver.id?.slice(0, 8)}
                  </span>
                  {ver.is_active && (
                    <span className="font-heading" style={{ fontSize: '9px', color: '#03f37c', letterSpacing: '0.06em' }}>
                      ACTIVE
                    </span>
                  )}
                  {ver.is_immutable && (
                    <span className="font-heading" style={{ fontSize: '9px', color: '#03f37c', letterSpacing: '0.06em' }}>
                      LOCKED
                    </span>
                  )}
                  {ver.admission_state && (
                    <AdmissionBadge state={ver.admission_state} />
                  )}
                </div>
                <p className="font-body text-gray-500 mt-1" style={{ fontSize: '11px' }}>
                  {ver.content_summary || 'No summary available'}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <p className="font-body text-gray-400 text-sm">
              Canon source linkage not yet established.
              Lineage is populated after the artifact reaches CANON_LOCKED state.
            </p>
            <p className="font-body text-gray-400 mt-1" style={{ fontSize: '11px', fontStyle: 'italic' }}>
              {lineage_disclosure}
            </p>
          </div>
        )}
      </Section>

      {/* Passages */}
      <Section title={`EXTRACTED PASSAGES (${passages.length})`} testId="section-passages">
        <p className="font-body text-gray-400 mb-3" style={{ fontSize: '11px' }}>
          Passage count: {artifact.passage_count ?? passages.length}.
          Anchors and hashes reflect actual extraction results.
        </p>
        <PassageList passages={passages} />
      </Section>

      {/* Claims */}
      <Section title={`DERIVED CLAIMS (${(claims || []).length})`} testId="section-claims">
        {(claims || []).length === 0 ? (
          <p className="font-body text-gray-400 text-sm">
            {canon_source
              ? 'No claim candidates extracted yet. Use Evidence → Extract candidates on the CANON_LOCKED artifact.'
              : 'Claims are only available after CANON_LOCKED state and extraction.'}
          </p>
        ) : (
          <div className="space-y-2">
            {(claims || []).slice(0, 10).map((c, i) => (
              <div key={c.id || i} className="rounded border p-3" style={{ borderColor: '#e5e5e5' }} data-testid={`claim-item-${i}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-heading font-medium text-black" style={{ fontSize: '11px' }}>
                    {c.claim_type || 'CLAIM'}
                  </span>
                  <span className="font-body text-gray-400" style={{ fontSize: '11px' }}>
                    {c.review_state || 'UNREVIEWED'}
                  </span>
                </div>
                <p className="font-body text-gray-700" style={{ fontSize: '12px' }}>{c.text}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Constraints */}
      <Section title={`DERIVED CONSTRAINTS (${(constraints || []).length})`} testId="section-constraints">
        {(constraints || []).length === 0 ? (
          <p className="font-body text-gray-400 text-sm">
            {canon_source ? 'No constraint candidates extracted yet.' : 'Constraints available after CANON_LOCKED + extraction.'}
          </p>
        ) : (
          <div className="space-y-2">
            {(constraints || []).slice(0, 10).map((c, i) => (
              <div key={c.id || i} className="rounded border p-3" style={{ borderColor: '#e5e5e5' }} data-testid={`constraint-item-${i}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-heading font-medium text-black" style={{ fontSize: '11px' }}>CONSTRAINT</span>
                  <span className="font-body text-gray-400" style={{ fontSize: '11px' }}>{c.review_state || 'UNREVIEWED'}</span>
                </div>
                <p className="font-body text-gray-700" style={{ fontSize: '12px' }}>{c.text}</p>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Human review actions */}
      <Section title="HUMAN REVIEW" icon={CheckSquare} testId="section-review-actions">
        <div className="mb-3 p-3 rounded border" style={{ borderColor: '#d9dee6', backgroundColor: '#fafafa' }}>
          <p className="font-body text-gray-600" style={{ fontSize: '12px' }}>
            <strong className="font-heading font-medium text-black">Review actions</strong> route this artifact for governed human review.
            ACCEPT_FOR_EVIDENCE_REVIEW does NOT mean accepted institutional evidence, CANON_LOCKED, or scientifically validated.
            REJECT, HOLD, and ESCALATE require a note.
            Reviewer identity is preview-supplied — KS-GATE-AUTH remains open.
          </p>
        </div>
        <button
          onClick={() => setShowReview(v => !v)}
          className="flex items-center gap-1.5 font-heading font-medium text-black mb-4"
          style={{ fontSize: '12px' }}
          data-testid="toggle-review-form"
        >
          {showReview ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {showReview ? 'Hide review form' : 'Open review form'}
        </button>
        {showReview && (
          <ReviewActionForm artifactId={id} onSuccess={() => { setShowReview(false); load(); }} />
        )}
      </Section>

      {/* Review history */}
      <Section title="REVIEW HISTORY" icon={Clock} testId="section-review-history">
        <p className="font-body text-gray-400 mb-3" style={{ fontSize: '11px' }}>
          Append-only. Entries are never deleted or modified.
        </p>
        <ReviewHistory history={review_history} />
      </Section>

      {/* Audit events */}
      <Section title="AUDIT EVENTS" testId="section-audit-events">
        <p className="font-body text-gray-400 mb-3" style={{ fontSize: '11px' }}>
          Machine and human-initiated events linked to this artifact.
        </p>
        <AuditEventList events={audit_events} />
      </Section>

      {/* Navigation links */}
      <Section title="RELATED RECORDS" testId="section-related-records">
        <div className="flex flex-wrap gap-3">
          <Link
            to="/app/canon"
            className="inline-flex items-center gap-1.5 font-heading font-medium text-black border border-gray-200 rounded px-3 py-2 hover:border-gray-400 transition-colors"
            style={{ fontSize: '12px' }}
            data-testid="link-to-canon"
          >
            Canon sources
          </Link>
          <Link
            to="/app/evidence"
            className="inline-flex items-center gap-1.5 font-heading font-medium text-black border border-gray-200 rounded px-3 py-2 hover:border-gray-400 transition-colors"
            style={{ fontSize: '12px' }}
            data-testid="link-to-evidence"
          >
            Evidence graph
          </Link>
          <Link
            to="/app/sentinel"
            className="inline-flex items-center gap-1.5 font-heading font-medium text-black border border-gray-200 rounded px-3 py-2 hover:border-gray-400 transition-colors"
            style={{ fontSize: '12px' }}
            data-testid="link-to-sentinel"
          >
            Sentinel
          </Link>
          {canon_source && (
            <Link
              to={`/app/canon/${canon_source.id}`}
              className="inline-flex items-center gap-1.5 font-heading font-medium px-3 py-2 rounded border transition-colors"
              style={{ fontSize: '12px', backgroundColor: '#000', color: '#03f37c', borderColor: '#000' }}
              data-testid="link-to-canon-source"
            >
              View canon source →
            </Link>
          )}
        </div>
      </Section>
    </div>
  );
}
